<script lang="ts" setup>
import {computed} from 'vue';
import {ReccoMediaType} from '@/ts/types/component/recco/media.type';
import MediaControlButton from '@/vue/atoms/media-control-button.vue';
import {formatTime} from '@/ts/composables/stateful/use-media-player';

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

const currentTime = defineModel<number>('currentTime', {required: true});

const props = withDefaults(defineProps<{
  isPlaying: boolean;
  isMuted: boolean;
  duration: number;
  mediaType: ReccoMediaType;
  formattedCurrentTime?: string;
  formattedDuration?: string;
  showVolumeControl?: boolean;
  showPlaybackSpeed?: boolean;
  playbackRate?: number;
}>(), {
  showVolumeControl: false,
  showPlaybackSpeed: false,
  playbackRate: 1,
});

const emit = defineEmits<{
  clickPlay: [];
  clickPause: [];
  clickFullscreen: [];
  clickToggleMute: [];
  changePlaybackRate: [rate: number];
}>();

const fallbackFormattedCurrentTime = computed((): string => {
  return props.formattedCurrentTime ?? formatTime(currentTime.value);
});

const fallbackFormattedDuration = computed((): string => {
  return props.formattedDuration ?? formatTime(props.duration);
});

const displayCenteredControls = computed((): boolean => {
  return props.mediaType === ReccoMediaType.Audio || !props.isPlaying;
});

const isAudio = computed((): boolean => {
  return props.mediaType === ReccoMediaType.Audio;
});


const onClickLittleBack = (): void => {
  currentTime.value = Math.max(currentTime.value - 10, 0);
};

const onClickLittleForward = (): void => {
  currentTime.value = Math.min(currentTime.value + 10, props.duration);
};

function cyclePlaybackRate(): void {
  const currentIndex = PLAYBACK_RATES.indexOf(props.playbackRate);
  const nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length;
  emit('changePlaybackRate', PLAYBACK_RATES[nextIndex]);
}

function formatPlaybackRate(rate: number): string {
  return rate === 1 ? '1x' : `${rate}x`;
}

</script>

<template>
  <div class="media-controls-overlay">
    <div v-if="displayCenteredControls" class="centered-controls">
      <media-control-button
        v-if="!isPlaying"
        type="play"
        @click="emit('clickPlay')"
      />
      <media-control-button
        v-if="isPlaying"
        type="pause"
        @click="emit('clickPause')"
      />
      <media-control-button
        display-flat
        type="skipBackward"
        class="order-first"
        @click="onClickLittleBack"
      />
      <media-control-button
        display-flat
        type="skipForward"
        class="order-last"
        @click="onClickLittleForward"
      />
    </div>

    <div>
      <div class="button-bar">
        <div class="button-bar__group">
          <media-control-button
            v-if="mediaType === ReccoMediaType.Video && isPlaying"
            display-flat
            type="pause"
            @click="emit('clickPause')"
          />
        </div>
        <div class="button-bar__group">
          <media-control-button
            v-if="!displayCenteredControls"
            display-flat
            type="skipBackward"
            @click="onClickLittleBack"
          />
          <media-control-button
            v-if="!displayCenteredControls"
            display-flat
            type="skipForward"
            @click="onClickLittleForward"
          />
          <media-control-button
            v-if="showVolumeControl"
            display-flat
            :type="isMuted ? 'unmute' : 'mute'"
            @click="emit('clickToggleMute')"
          />
          <media-control-button
            v-if="showPlaybackSpeed"
            display-flat
            type="speed"
            @click="cyclePlaybackRate"
          >
            {{ formatPlaybackRate(playbackRate) }}
          </media-control-button>
          <media-control-button
            v-if="!isAudio"
            display-flat
            type="fullscreen"
            @click="emit('clickFullscreen')"
          />
        </div>
      </div>

      <q-slider
        v-model="currentTime"
        :max="duration || 1"
        color="white"
      />

      <div class="duration">
        <span class="text-white text-overline">{{ fallbackFormattedCurrentTime }}</span>
        <span class="text-white text-overline">{{ fallbackFormattedDuration }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.media-controls-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0 var(--Spacing-spacing-4);
}

.button-bar {
  display: flex;
  justify-content: space-between;
  margin: 0 -7px;

  &__group {
    display: flex;
    gap: 2px;

    @include screen-sm {
      gap: 5px;
    }
  }
}

// Backs the centred controls, which had none: the bottom gradient stops short of the middle and
// the skip buttons are flat, so their near-white glyphs read 1.12:1 on the pale frame a video ends on.
.centered-controls {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: var(--Spacing-spacing-2);
  border-radius: 999px;
  @include media-surface-overlay;

  @include screen-sm {
    gap: 15px;
  }

  :deep(.btn) {
    width: var(--Spacing-spacing-7);
    height: var(--Spacing-spacing-7);
    min-width: unset;
    min-height: unset;
  }
}

.duration {
  display: none;
  margin-top: -10px;

  @include screen-sm {
    display: flex;
    justify-content: space-between;
  }
}


</style>
